// This file contains the custom Axios instance that is used to make requests to the backend.
// Code copied from Orval examples:
// https://github.com/orval-labs/orval/blob/master/samples/react-query/basic/src/auth.context.tsx
// https://github.com/orval-labs/orval/blob/master/samples/react-query/custom-client/src/api/mutator/custom-client.ts

import Axios, { AxiosError, AxiosRequestConfig } from "axios";
import { signOut } from "firebase/auth";
import { toast } from "sonner";
import { auth } from "@/auth/firebase";

export const getBaseUrl = () => {
  switch (import.meta.env.MODE) {
    case "prod":
      return "https://nomp-prod-150239024349.us-west1.run.app";
    case "staging":
      return "https://nomp-staging-856431008964.us-west1.run.app";
    case "demo":
      return "https://nomp-demo-619048117804.us-west1.run.app";
    default:
      return "http://localhost:8000";
  }
};

export const AXIOS_INSTANCE = Axios.create({
  baseURL: getBaseUrl(),
  withCredentials: true,
  paramsSerializer: {
    indexes: null, // Converts arrays to a readable format for FastAPI
  },
});

AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const detail = (error.response?.data as { detail?: string })?.detail;
    if (
      error.response?.status === 401 &&
      detail === "Session expired due to inactivity" &&
      auth.currentUser
    ) {
      toast.warning("You have been logged out due to inactivity.", {
        id: "timeout-logout",
      });
      await signOut(auth);
    }
    return Promise.reject(error);
  },
);

export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
  const promise = (async () => {
    // Wait for Firebase to restore the persisted session before reading the
    // user. Route loaders can fire before onAuthStateChanged first resolves,
    // which would otherwise send `Bearer undefined` and get a 403.
    await auth.authStateReady();

    // Get the current firebase user's ID token
    const token = await auth.currentUser?.getIdToken();

    // Add the token to the request headers
    const headers = {
      ...config.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    // Make the request
    const { data } = await AXIOS_INSTANCE({
      ...config,
      headers,
    });

    return data;
  })();

  return promise;
};

export interface ErrorType<Error> extends AxiosError<Error> {}
